/**
 * **The edit page shell** (C13, spec §10.5 item 3; interface I-1 of Wave 14).
 *
 * A back link, a title, one line of what the page is for, and the page's
 * content in a column wide enough for a two-column form and no wider. The page
 * puts its `EditBar` last among its children, so the bar sticks to the bottom
 * of this column and spans exactly its width.
 *
 *     <EditPage title="Navigation" description="…" back={{ to: '/site', label: 'Your website' }}>
 *       {guard}
 *       …cards…
 *       <EditBar dirty={dirty} saving={saving} onSave={save} previewHref={previewHref} />
 *     </EditPage>
 */

import { PageHeader } from '@fiveox/ui'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

export interface EditPageBack {
  /** Where the back link goes — `/site`, `/settings`, a list. */
  to: string
  /** What it reads — *Your website*, *Settings*, *Your pages*. */
  label: string
}

export interface EditPageProps {
  title: ReactNode
  description?: ReactNode
  back: EditPageBack
  children: ReactNode
  /** Secondary actions at the top right — never a second Save. */
  actions?: ReactNode
  /** Under the title: a badge, a status line. */
  meta?: ReactNode
}

export function EditPage({ title, description, back, children, actions, meta }: EditPageProps) {
  return (
    <div data-edit-page="" className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader
        back={
          <Link
            to={back.to}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 font-medium"
          >
            <ArrowLeft size={14} aria-hidden /> {back.label}
          </Link>
        }
        title={title}
        description={description}
        actions={actions}
        meta={meta}
      />
      {children}
    </div>
  )
}
